import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Star, ArrowRight, ArrowLeft } from "lucide-react";
import { PricingService, PricingPlan as PricingPlanType } from "@/services/pricingService";
import { useAuth } from "@/hooks/useAuth";
import TopNavbar from "@/components/layout/TopNavbar";
import Footer from "@/components/home/Footer";
import HoneycombBackground from "@/components/ui/honeycomb-background";

const PricingPlan = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plan, setPlan] = useState<PricingPlanType | null>(null);
  const [allPlans, setAllPlans] = useState<PricingPlanType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPricingPlan();
  }, [slug]);

  const loadPricingPlan = async () => {
    if (!slug) {
      setError('Plan not found');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [plans, specificPlan] = await Promise.all([
        PricingService.getGlobalPricingPlans(),
        PricingService.getPricingPlanBySlug(slug)
      ]);
      
      setAllPlans(plans);
      setPlan(specificPlan);
      
      if (!specificPlan) {
        setError('Plan not found');
      }
    } catch (err) {
      setError('Failed to load pricing plan');
      console.error('Error loading pricing plan:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePlanSelect = () => {
    if (!plan) return;
    
    // Check if plan is auto-subscribable
    if (!plan.auto_subscribe) {
      // For non-auto-subscribable plans, go to contact sales
      navigate('/contact');
      return;
    }
    
    if (user) {
      navigate('/subscription-checkout', { 
        state: { 
          priceSlug: plan.slug,
          stripePriceId: plan.stripe_price_id 
        } 
      });
    } else {
      navigate('/signup', { 
        state: { 
          returnTo: '/subscription-checkout',
          priceSlug: plan.slug 
        } 
      });
    }
  };

  const getOtherPlans = () => {
    return allPlans.filter(p => p.slug !== slug);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <TopNavbar />
        <HoneycombBackground className="opacity-10" simplified />
        <div className="relative z-10 pt-16">
          <div className="py-16">
            <div className="max-w-4xl mx-auto px-4 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FA8C00] mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading pricing plan...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !plan) {
    return (
      <div className="min-h-screen bg-white">
        <TopNavbar />
        <HoneycombBackground className="opacity-10" simplified />
        <div className="relative z-10 pt-16">
          <div className="py-16">
            <div className="max-w-4xl mx-auto px-4 text-center">
              <h1 className="text-2xl font-bold text-gray-900 mb-4">Plan Not Found</h1>
              <p className="text-gray-600 mb-8">The pricing plan you're looking for doesn't exist.</p>
              <Button onClick={() => navigate('/pricing')} variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" />
                View All Plans
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isPopular = plan.metadata?.tier === 'popular';
  const otherPlans = getOtherPlans();

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{plan.name} Plan - Hivetivity | {PricingService.formatPrice(plan.amount_cents, plan.currency, plan.billing_period)}</title>
        <meta 
          name="description" 
          content={`${plan.name} plan for ${PricingService.formatPrice(plan.amount_cents, plan.currency, plan.billing_period)}. ${plan.description || 'Perfect for growing youth programs.'} Start your 14-day free trial.`}
        />
        <link rel="canonical" href={`https://hivetivity.com/pricing/${plan.slug}`} />
      </Helmet>
      
      <TopNavbar />
      <HoneycombBackground className="opacity-10" simplified />
      
      <div className="relative z-10 pt-16">
        {/* Breadcrumb */}
        <div className="py-4 px-4">
          <div className="max-w-6xl mx-auto">
            <button
              onClick={() => navigate('/pricing')}
              className="flex items-center text-sm text-gray-600 hover:text-[#FA8C00] transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to All Plans
            </button>
          </div>
        </div>

        {/* Hero Section */}
        <section className="py-12 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Plan Details */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="relative">
                  {isPopular && (
                    <Badge className="bg-[#FA8C00] text-white px-4 py-1 mb-4 flex items-center gap-1 w-fit">
                      <Star className="h-3 w-3" />
                      Most Popular
                    </Badge>
                  )}
                  
                  <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                    {plan.name} Plan
                  </h1>
                  
                  {plan.description && (
                    <p className="text-xl text-gray-600 mb-6">{plan.description}</p>
                  )}
                  
                  <div className="mb-6">
                    <span className="text-5xl font-bold text-gray-900">
                      {PricingService.formatPrice(plan.amount_cents, plan.currency, plan.billing_period)}
                    </span>
                  </div>
                  
                  {plan.trial_period_days > 0 && (
                    <p className="text-lg text-[#FA8C00] font-medium mb-8">
                      {PricingService.getTrialText(plan.trial_period_days)}
                    </p>
                  )}
                  
                  <Button
                    onClick={handlePlanSelect}
                    size="lg"
                    variant={plan.auto_subscribe ? 'default' : 'outline'}
                    className={`px-8 py-4 text-lg ${
                      plan.auto_subscribe 
                        ? 'bg-[#FA8C00] hover:bg-[#FA8C00]/90 text-white' 
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {PricingService.getCallToActionText(plan).text} {plan.auto_subscribe ? `with ${plan.name}` : ''}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
              </motion.div>

              {/* Plan Card */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Card className={`transition-all duration-300 ${
                  isPopular ? 'border-[#FA8C00] border-2 shadow-xl' : 'border-gray-200 shadow-lg'
                }`}>
                  <CardHeader className="text-center pb-4">
                    <CardTitle className="text-2xl font-bold text-gray-900">
                      What's Included
                    </CardTitle>
                  </CardHeader>

                  <CardContent>
                    {plan.features && plan.features.length > 0 ? (
                      <div>
                        {/* Feature inheritance prefix */}
                        {PricingService.getFeaturePrefix(plan) && (
                          <p className="text-base font-medium text-gray-800 mb-4">
                            {PricingService.getFeaturePrefix(plan)}
                          </p>
                        )}
                        
                        <ul className="space-y-4">
                          {plan.features.map((feature, index) => (
                            <li key={index} className="flex items-start gap-3">
                              <CheckCircle className="h-6 w-6 text-green-500 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-700 text-lg">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <p className="text-gray-600 text-center py-8">
                        All core features included
                      </p>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Compare Plans */}
        {otherPlans.length > 0 && (
          <section className="py-16 px-4 bg-gray-50">
            <div className="max-w-6xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">
                    Compare with Other Plans
                  </h2>
                  <p className="text-lg text-gray-600">
                    See how the {plan.name} plan compares to our other options
                  </p>
                </div>

                <div className={`grid gap-8 ${otherPlans.length === 1 ? 'md:grid-cols-2' : 'md:grid-cols-3'} max-w-5xl mx-auto`}>
                  {/* Current Plan */}
                  <div className="relative">
                    <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-[#FA8C00] text-white px-3 py-1">
                      Current
                    </Badge>
                    <Card className="border-[#FA8C00] border-2 bg-[#FA8C00]/5">
                      <CardHeader className="text-center pb-4">
                        <CardTitle className="text-xl font-bold text-gray-900">
                          {plan.name}
                        </CardTitle>
                        <div className="text-2xl font-bold text-[#FA8C00] mt-2">
                          {PricingService.formatPrice(plan.amount_cents, plan.currency, plan.billing_period)}
                        </div>
                      </CardHeader>
                      <CardContent>
                        <Button
                          onClick={handlePlanSelect}
                          variant={plan.auto_subscribe ? 'default' : 'outline'}
                          className={`w-full ${
                            plan.auto_subscribe 
                              ? 'bg-[#FA8C00] hover:bg-[#FA8C00]/90 text-white' 
                              : 'border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {PricingService.getCallToActionText(plan).text}
                        </Button>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Other Plans */}
                  {otherPlans.map((otherPlan) => (
                    <Card key={otherPlan.id} className="border-gray-200 hover:shadow-lg transition-shadow">
                      <CardHeader className="text-center pb-4">
                        <CardTitle className="text-xl font-bold text-gray-900">
                          {otherPlan.name}
                        </CardTitle>
                        <div className="text-2xl font-bold text-gray-900 mt-2">
                          {PricingService.formatPrice(otherPlan.amount_cents, otherPlan.currency, otherPlan.billing_period)}
                        </div>
                      </CardHeader>
                      <CardContent>
                        <Button
                          onClick={() => navigate(`/pricing/${otherPlan.slug}`)}
                          variant="outline"
                          className="w-full"
                        >
                          View Details
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                
                <div className="text-center mt-8">
                  <Button
                    onClick={() => navigate('/pricing')}
                    variant="outline"
                    className="px-8"
                  >
                    Compare All Plans
                  </Button>
                </div>
              </motion.div>
            </div>
        </section>
        )}

        <Footer />
      </div>
    </div>
  );
};

export default PricingPlan;